/** The first-run journey for an Admin: five steps in the order a filing is reached, each one's
 * done-state DERIVED from what the app already serves.
 *
 * **No step is a stored flag.** Nothing here is ticked by a person and nothing is remembered: every
 * state is recomputed from the companies, the sites, the declared periods, the figures, the
 * determinations and the reports on each render, so a step that stops being true (a site removed,
 * a determination superseded) un-ticks itself. A stored "completed" would be ADR-0030's `is_current`
 * column under another name.
 *
 * **Three answers per fact, not two.** `unknown` is "not read yet, or could not be read" and is
 * never `done` and never `todo`: the checklist claims nothing about it. A dropped request turning
 * into *"you have not stated your financials"* is the defect this product has fixed a dozen times.
 *
 * **This is the only first-run card.** It replaced a four-step structural card (company, period,
 * site, activity) whose last two steps this one already asked: the structural answers are still
 * given, as the *Still to add* line under the sites step (`sitesStillToAdd`), so no information
 * was dropped, only the second card.
 *
 * This file exports no component (fast refresh's one-kind-of-export rule). The derivations are
 * pure and testable without a tree; `useFirstRunJourney` is the one hook that reads the facts
 * and is used by Readiness, which hands its result to the card.
 *
 * Nothing here is a regulatory sentence. What a regime is called, whether it applies and why come
 * from the served obligation rows and are rendered by `FirstRunChecklist.tsx`.
 */

import { useFacilities } from "../api/evidence";
import { useEntities, useObligations } from "../api/queries";
import type { Obligation, Scope2Method } from "../api/queries";
import { stageOfVerdict } from "./readinessModel";
import { useFilingReports } from "./readinessReports";

export type ChecklistKey = "financials" | "regimes" | "sites" | "activity" | "outstanding";

export const CHECKLIST_ORDER: readonly ChecklistKey[] = [
  "financials",
  "regimes",
  "sites",
  "activity",
  "outstanding",
];

export type Fact = "done" | "todo" | "unknown";

/** `unchecked` is a fact that was not (or could not be) read: not done, and not offered as next. */
export type ChecklistState = "done" | "next" | "waiting" | "unchecked";

export interface ChecklistStep {
  key: ChecklistKey;
  state: ChecklistState;
}

/** In order, with the first step that is not done marked `next` and every later one `waiting`.
 * An `unknown` step is `unchecked` and also stops the walk: the step after it is not offered on the
 * strength of a fact nobody has seen. */
export function checklistSteps(facts: Record<ChecklistKey, Fact>): ChecklistStep[] {
  let found = false;
  return CHECKLIST_ORDER.map((key) => {
    const fact = facts[key];
    if (fact === "done") return { key, state: "done" };
    if (fact === "unknown") {
      found = true;
      return { key, state: "unchecked" };
    }
    const state: ChecklistState = found ? "waiting" : "next";
    found = true;
    return { key, state };
  });
}

export interface ChecklistInputs {
  /** `undefined` while the read is in flight or has failed. */
  entityCount: number | undefined;
  facilityCount: number | undefined;
  hasPeriod: boolean;
  /** Readiness's own tri-state for "has the period's figures": `unknown` is in flight or failed. */
  activity: "present" | "absent" | "unknown";
  obligations: readonly Obligation[] | undefined;
  /** The verdict of each owed filing's report, aligned with `owedFilings(obligations)`;
   * `undefined` while any is in flight or has failed. */
  verdicts: readonly string[] | undefined;
}

/** A filing that is owed: the same definition Readiness's "Must file" count uses. */
export function owedFilings(obligations: readonly Obligation[]): Obligation[] {
  return obligations.filter((o) => o.status === "required" && !o.undetermined);
}

/** Whether a report's verdict genuinely keeps a first filing from being made.
 *
 * The ladder is `not_evaluated < unsupported < incomplete < checks_passed < approved < issued`.
 * **Everything from `checks_passed` up has passed its checks**, and "checks passed, not yet
 * issued" on an otherwise set-up workspace is the ordinary state of a filing waiting for a
 * person, not something left to set up. `incomplete` blocks (a failing rule, a census gap) and
 * `not_evaluated` blocks (nothing was checked, which must never read as success).
 *
 * **`unsupported` does not block *this checklist*.** It means every disclosure the regime requires
 * is one the product cannot produce, so nothing the Admin could add here would change it; keeping
 * the card open for it would make the card unfinishable on a workspace with such a regime. The
 * filing is not hidden by this: Readiness's own filing card still prints it as "Not supported".
 * An unrecognised verdict string blocks, as it did before: only a known word clears. */
export function blocksFirstFiling(verdict: string): boolean {
  return verdict !== "unsupported" && !stageOfVerdict(verdict).passed;
}

/** The owed filings that still block a first filing, in the order the owed list is in. Only
 * meaningful when `verdicts` is aligned with `owedFilings`. */
export function outstandingFilings(
  obligations: readonly Obligation[],
  verdicts: readonly string[],
): Obligation[] {
  return owedFilings(obligations).filter((_, index) => blocksFirstFiling(verdicts[index] ?? ""));
}

export type SiteGap = "company" | "period" | "site";

/** What the sites step is still waiting for, named separately - the one thing the retired
 * four-step card said that this one did not. `undefined` counts are unread, and claim nothing. */
export function sitesStillToAdd(
  input: Pick<ChecklistInputs, "entityCount" | "facilityCount" | "hasPeriod">,
): SiteGap[] {
  if (input.entityCount === undefined || input.facilityCount === undefined) return [];
  const gaps: SiteGap[] = [];
  if (input.entityCount === 0) gaps.push("company");
  if (!input.hasPeriod) gaps.push("period");
  if (input.facilityCount === 0) gaps.push("site");
  return gaps;
}

export function checklistFacts(input: ChecklistInputs): Record<ChecklistKey, Fact> {
  const { obligations } = input;

  const financials: Fact = obligations === undefined ? "unknown" : obligations.length > 0 ? "done" : "todo";

  // "Which regimes apply" is answered only when every position has an answer: a position still
  // waiting on a missing fact that could change it is not an answer.
  const regimes: Fact =
    obligations === undefined
      ? "unknown"
      : obligations.length === 0 || obligations.some((o) => o.undetermined)
        ? "todo"
        : "done";

  const sites: Fact =
    input.entityCount === undefined || input.facilityCount === undefined
      ? "unknown"
      : input.entityCount > 0 && input.facilityCount > 0 && input.hasPeriod
        ? "done"
        : "todo";

  const activity: Fact =
    input.activity === "present" ? "done" : input.activity === "absent" ? "todo" : "unknown";

  let outstanding: Fact;
  if (obligations === undefined) outstanding = "unknown";
  else if (obligations.length === 0 || obligations.some((o) => o.undetermined)) outstanding = "todo";
  else if (owedFilings(obligations).length === 0) outstanding = "done";
  else if (input.verdicts === undefined) outstanding = "unknown";
  else outstanding = outstandingFilings(obligations, input.verdicts).length === 0 ? "done" : "todo";

  return { financials, regimes, sites, activity, outstanding };
}

export type JourneyPlan =
  | { kind: "steps"; steps: ChecklistStep[]; rows: readonly Obligation[]; owed: Obligation[];
      verdicts: readonly string[] | undefined; outstanding: Obligation[];
      entityCount: number; facilityCount: number; hasPeriod: boolean }
  | { kind: "unread"; error: unknown; retry: () => void }
  | null;

/** The journey for Readiness: the plan the card renders, and whether the structural set-up
 * (sites and a period, or the first figure) is still missing.
 *
 * `setupIncomplete` keeps the meaning the retired card's plan had for Readiness's attention list
 * ("Nothing can be checked until this workspace is set up"): true only when the sites step or the
 * activity step is *known* to be missing. An unread fact is not an incomplete set-up.
 *
 * The plan is `null` while the reads that decide the first four steps are in flight, and when
 * every step is done. Report reads share `useReport`'s query keys, so calling this once in
 * Readiness costs nothing the screen was not already paying. */
export function useFirstRunJourney(
  hasPeriod: boolean,
  activity: ChecklistInputs["activity"],
  method: Scope2Method,
): { plan: JourneyPlan; setupIncomplete: boolean; settled: boolean } {
  const entities = useEntities();
  const facilities = useFacilities();
  const obligations = useObligations();
  const rows = obligations.data;
  const owed = rows ? owedFilings(rows) : [];
  const reports = useFilingReports(owed, method);

  // Decided from the companies and sites alone, so a failed determinations read does not turn
  // "this workspace has no site" into "nothing to say".
  const setupIncomplete =
    entities.data !== undefined && facilities.data !== undefined
      ? checklistFacts({
          entityCount: entities.data.length,
          facilityCount: facilities.data.length,
          hasPeriod,
          activity,
          obligations: undefined,
          verdicts: undefined,
        }).sites === "todo" || activity === "absent"
      : false;

  const failed =
    entities.error ?? facilities.error ?? obligations.error ?? reports.find((r) => r.error)?.error;
  if (failed) {
    return {
      plan: {
        kind: "unread",
        error: failed,
        retry: () => {
          void entities.refetch();
          void facilities.refetch();
          void obligations.refetch();
          for (const r of reports) if (r.error) void r.refetch();
        },
      },
      setupIncomplete,
      settled: true,
    };
  }
  // Nothing is claimed while the three reads that decide the first four steps are in flight, and
  // `settled` says so: a `null` plan is "still reading" and also "every step done", which a page that
  // lets the list lead has to tell apart.
  if (!rows || !entities.data || !facilities.data) return { plan: null, setupIncomplete, settled: false };

  const verdicts = reports.every((r) => r.data) ? reports.map((r) => r.data?.verdict ?? "") : undefined;
  const facts = checklistFacts({
    entityCount: entities.data.length,
    facilityCount: facilities.data.length,
    hasPeriod,
    activity,
    obligations: rows,
    verdicts,
  });
  const steps = checklistSteps(facts);
  if (steps.every((s) => s.state === "done")) return { plan: null, setupIncomplete, settled: true };
  return {
    plan: {
      kind: "steps",
      steps,
      rows,
      owed,
      verdicts,
      outstanding: verdicts ? outstandingFilings(rows, verdicts) : [],
      entityCount: entities.data.length,
      facilityCount: facilities.data.length,
      hasPeriod,
    },
    setupIncomplete,
    settled: true,
  };
}
